if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.addEventListener('keydown', this.onGalleryKeydown.bind(this));
        this.dataset.sliderEnhanced = 'true';
      }

      onGalleryKeydown(event) {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || !this.elements.thumbnails) return;
        const currentButton = event.target.closest('.thumbnail');
        if (!currentButton) return;

        const items = [...this.elements.thumbnails.querySelectorAll('[data-target]')].filter(
          (item) => item.offsetParent !== null
        );
        const currentItem = currentButton.closest('[data-target]');
        const currentIndex = items.indexOf(currentItem);
        if (currentIndex < 0) return;

        const nextIndex = event.key === 'ArrowRight' ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex < 0 || nextIndex >= items.length) return;

        event.preventDefault();
        const nextItem = items[nextIndex];
        this.setActiveMedia(nextItem.dataset.target, false);
        nextItem.querySelector('button')?.focus();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) {
          activeMedia = this.findLocalizedMedia(mediaId);
          prepend = false;
        }
        if (!activeMedia) return;
        mediaId = activeMedia.dataset.mediaId;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      findLocalizedMedia(mediaId) {
        const productRoot = this.closest('.product');
        let optionLabel = productRoot?.querySelector('.product-form__input input[type="radio"]:checked')?.value;
        const variantData = productRoot?.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]');

        if (variantData) {
          try {
            const originalMediaId = String(mediaId).split('-').pop();
            const variants = JSON.parse(variantData.textContent);
            const matchingVariant = variants.find(
              (variant) => String(variant.featured_media?.id) === originalMediaId
            );
            optionLabel = matchingVariant?.option1 || matchingVariant?.title || optionLabel;
          } catch (error) {
            // Keep the currently selected option as a safe fallback.
          }
        }

        if (!optionLabel) return null;
        const selectedLabel = this.normalizeMediaLabel(optionLabel);
        let bestMatch = null;
        let bestScore = 0;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((media) => {
          const imageLabel = this.normalizeMediaLabel(media.querySelector('img')?.alt);
          const score = this.mediaLabelScore(selectedLabel, imageLabel);
          if (score > bestScore) {
            bestMatch = media;
            bestScore = score;
          }
        });

        return bestScore >= 0.55 ? bestMatch : null;
      }

      normalizeMediaLabel(value) {
        return String(value || '')
          .normalize('NFKC')
          .toLowerCase()
          .replace(/ダブル差し込み|ダブルスロット/g, 'スタンド')
          .replace(/丸形|丸型/g, '丸')
          .replace(/ホログラム|フラッシュ/g, 'ホログラム')
          .replace(/キーホルダー|チャーム/g, 'チャーム')
          .replace(/acrylic key(?:chain|holder)|acrylic charm/g, 'acryliccharm')
          .replace(/double(?:-| )?(?:flash|hologram|holographic|shimmer)/g, 'doublehologram')
          .replace(/[^a-z0-9\u3040-\u30ff\u3400-\u9fff]/g, '');
      }

      mediaLabelScore(first, second) {
        const makePairs = (value) => {
          const pairs = [];
          for (let index = 0; index < value.length - 1; index += 1) pairs.push(value.slice(index, index + 2));
          return pairs;
        };
        const firstPairs = makePairs(first);
        const secondPairs = makePairs(second);
        if (!firstPairs.length || !secondPairs.length) return 0;

        const used = new Array(secondPairs.length).fill(false);
        let matches = 0;
        firstPairs.forEach((pair) => {
          const matchIndex = secondPairs.findIndex((candidate, index) => !used[index] && candidate === pair);
          if (matchIndex < 0) return;
          used[matchIndex] = true;
          matches += 1;
        });
        return (2 * matches) / (firstPairs.length + secondPairs.length);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
